import { PackageCheck, Printer } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  type Column,
} from '@/components/ui';
import { type DispensingRx, useDispensingQueue } from '@/features/admin-v2/api';
import { clinicDay, todayInClinic } from './dispensed-day';
import styles from './DispensaryPage.module.css';

const time = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

/**
 * RL-1643: prescriptions already dispensed (handed over, packed or
 * delivered), for one day (today by default). The API answers the newest
 * 100 dispensed prescriptions; the day is picked here from `dispensedAt`.
 */
export function DispensedTab({
  onOpen,
  onLabels,
}: {
  onOpen: (rx: DispensingRx) => void;
  onLabels: (rx: DispensingRx) => void;
}) {
  const { t } = useTranslation();
  const [day, setDay] = useState(todayInClinic());
  const list = useDispensingQueue('dispensed');
  const rows = list.data?.filter(
    (rx) => rx.dispensedAt && clinicDay(rx.dispensedAt) === day,
  );
  const columns: Column<DispensingRx>[] = [
    {
      key: 'rx',
      header: 'Rx',
      render: (rx) => <span className="mono">{rx.number}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (rx) => (
        <>
          <p className={styles.name}>{rx.patient.fullName}</p>
          <p className={styles.sub}>{rx.patient.identity ?? 'No ID shown'}</p>
        </>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (rx) => rx.items.map((i) => i.medicine).join(', '),
    },
    {
      key: 'when',
      header: t('dispensary.dispensedAt'),
      render: (rx) => time(rx.dispensedAt),
    },
    {
      key: 'status',
      header: 'Status',
      render: (rx) => (
        <div className={styles.badges}>
          <Badge tone="success" dot>
            {t(`dispensary.dispensedStatus.${rx.status}`, {
              defaultValue: rx.status,
            })}
          </Badge>
          {rx.source === 'refill' && (
            <Badge tone="info">{t('dispensary.refillBadge')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (rx) => (
        <div className={styles.rowActions}>
          <Button size="sm" onClick={() => onOpen(rx)}>
            {t('dispensary.viewDetails')}
          </Button>
          <Button
            size="sm"
            icon={<Printer size={14} />}
            onClick={() => onLabels(rx)}
          >
            {t('dispensary.reprintLabels')}
          </Button>
        </div>
      ),
    },
  ];
  return (
    <>
      <div className={styles.dayFilter}>
        <Field label={t('dispensary.dispensedDay')}>
          <Input
            type="date"
            value={day}
            max={todayInClinic()}
            onChange={(event) => setDay(event.target.value || todayInClinic())}
          />
        </Field>
      </div>
      <DataTable
        caption={t('dispensary.dispensedCaption')}
        columns={columns}
        rows={rows}
        rowKey={(rx) => rx.prescriptionId}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        empty={
          <EmptyState
            icon={<PackageCheck size={22} />}
            title={t('dispensary.noneDispensed')}
          />
        }
      />
    </>
  );
}
